/**
 * Crew mobile · Today view.
 *
 * Mobile-first single screen showing the crew member's day. Current task
 * is expanded with the primary actions (Start / Pause / Complete). Past
 * tasks collapse to a checkmark line. Future tasks tease.
 *
 * Wireframe: docs/wireframes/07-crew-mobile-today.html
 *
 * The state transitions wire into POST /tasks/:id/transition. Photo
 * capture and voice-to-text are M5.2.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Hammer,
  Loader2,
  MapPin,
  Play,
  RotateCcw,
  Truck,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CrewTodayResponse, CrewTodayTask, TaskType } from '@vari-crew/schemas';
import { useCrewToday } from '@/api/crew-today';
import { isQueuedResult, useTransitionTask } from '@/api/tasks';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { CrewViewPicker } from '@/components/crew/CrewViewPicker';
import { CrewProjectCard } from '@/components/crew/CrewProjectCard';
import { CrewPickupCard } from '@/components/crew/CrewPickupCard';
import { Button } from '@/components/ui/Button';
import { CrewAvatars } from '@/components/ui/CrewAvatars';
import { cn } from '@/lib/cn';
import { computeCrewScheduleStatus } from '@/lib/crewSchedule';

/** Crew can undo a mis-tapped Finish for this long after completing — mirrors the
 *  server's notification debounce so the pending "completed" ping is suppressed
 *  (demo #7). */
const UNDO_GRACE_MS = 2 * 60 * 1000;

/** Business-local 'HH:MM' window (per-day schedule) → '9:00am–4:00pm'. */
function fmtDayWindow(w: { start_time: string; end_time: string }): string {
  const f = (hm: string) => {
    const [h = 0, m = 0] = hm.split(':').map(Number);
    const ap = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')}${ap}`;
  };
  return `${f(w.start_time)}–${f(w.end_time)}`;
}

const TYPE_ICON: Record<TaskType, LucideIcon> = {
  pickup: Truck,
  install: Hammer,
  punch: Wrench,
  labor: User,
};

function timeOnly(iso: string | null): string {
  if (!iso) return '';
  return format(parseISO(iso), 'h:mma').toLowerCase();
}

function formatHoursMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function CrewToday() {
  const [searchParams] = useSearchParams();
  const { data: me } = useCurrentUser();
  const isManager = me?.role === 'supervisor' || me?.role === 'admin';
  // ?date=YYYY-MM-DD lets us preview a non-today date during dev / demo
  const dateOverride = searchParams.get('date') ?? undefined;
  // Supervisors/admins can view another crew member's day (?crew_member_id).
  const viewAsId = isManager ? (searchParams.get('crew_member_id') ?? undefined) : undefined;
  const { data, isLoading, error } = useCrewToday(dateOverride, viewAsId);
  const navigate = useNavigate();
  const viewingOther = isManager && !!viewAsId && !!data?.crew_member;

  const sortedTasks = useMemo<CrewTodayTask[]>(() => {
    if (!data) return [];
    return [...data.tasks].sort((a, b) => {
      const aT = a.scheduled_start ?? '';
      const bT = b.scheduled_start ?? '';
      return aT.localeCompare(bT);
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full grid place-items-center p-6 text-status-blocked-fg text-sm">
        {(error as Error).message}
      </div>
    );
  }
  if (!data?.crew_member) {
    // A manager who hasn't picked anyone (or picked an unknown id) gets the
    // picker + a prompt; a non-crew user still gets the friendly empty state.
    if (isManager) {
      return (
        <div className="min-h-full bg-fill-soft pb-8">
          <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 pb-6 space-y-4">
            <CrewViewPicker />
            <div className="rounded-2xl border border-line bg-bg p-6 text-center text-sm text-ink-dim">
              Pick a crew member to see their day.
            </div>
          </div>
        </div>
      );
    }
    return <NoCrewMemberState role={me?.role} />;
  }

  return (
    <div className="min-h-full bg-fill-soft pb-8">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 pb-6 space-y-4">
        {isManager && <CrewViewPicker />}
        <Header data={data} viewingOther={viewingOther} />
        <ProgressPill data={data} totalTasks={sortedTasks.length} />
        {/* Projects worked directly (D-3/A3) lead the day; tasks (if any) are the
            optional checklist beneath. */}
        {data.projects.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">
              {data.projects.length === 1 ? 'Project' : 'Projects'}
            </div>
            <div className="space-y-2">
              {data.projects.map((p) => (
                <CrewProjectCard
                  key={p.block_id}
                  project={p}
                  expanded
                  canAct
                  onOpen={() => navigate(`/today/projects/${p.project_id}`)}
                />
              ))}
            </div>
          </div>
        )}
        {/* Pickups (workstream C) — warehouse runs beside the projects. */}
        {data.pickups.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">
              {data.pickups.length === 1 ? 'Pickup' : 'Pickups'}
            </div>
            <div className="space-y-2">
              {data.pickups.map((p) => (
                <CrewPickupCard
                  key={p.block_id}
                  pickup={p}
                  expanded
                  onOpen={() => navigate(`/today/pickups/${p.pickup_id}`)}
                />
              ))}
            </div>
          </div>
        )}
        <TaskList
          tasks={sortedTasks}
          activeTaskId={data.active_task_id}
          nextTaskId={data.next_task_id}
          navigate={navigate}
          hasProjects={data.projects.length > 0 || data.pickups.length > 0}
        />
      </div>
    </div>
  );
}

function Header({ data, viewingOther }: { data: CrewTodayResponse; viewingOther?: boolean }) {
  const displayName = data.crew_member!.user.display_name;
  const firstName = data.crew_member?.user.given_name ?? displayName.split(' ')[0] ?? 'there';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <header className="flex items-end justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-faint">
          {viewingOther ? 'Crew view' : 'Today'} · {format(parseISO(data.date), 'EEE MMM d')}
        </div>
        <h1 className="text-2xl font-semibold mt-0.5">
          {viewingOther ? `${displayName}’s day` : `Hi, ${firstName} 👋`}
        </h1>
      </div>
      <div className="w-9 h-9 rounded-full bg-fill flex items-center justify-center text-xs font-bold border border-line">
        {initials}
      </div>
    </header>
  );
}

function ProgressPill({ data, totalTasks }: { data: CrewTodayResponse; totalTasks: number }) {
  if (totalTasks === 0) {
    // Projects worked directly (D-3/A3) are the day's work even with no tasks.
    const projects = data.projects;
    if (projects.length > 0) {
      const active = projects.filter((p) => ['en_route', 'on_site', 'punch'].includes(p.status)).length;
      const finished = projects.filter((p) => p.status === 'pending_closeout' || p.status === 'complete').length;
      const label =
        active > 0
          ? `${projects.length === 1 ? 'Project' : `${projects.length} projects`} · in progress`
          : finished === projects.length
            ? `${projects.length === 1 ? 'Project' : `All ${projects.length} projects`} finished · nice work`
            : `${projects.length === 1 ? '1 project' : `${projects.length} projects`} today`;
      return (
        <div
          className={cn(
            'px-4 py-2.5 rounded-full text-center text-xs font-semibold',
            active > 0 ? 'bg-status-onsite text-status-onsite-fg' : 'bg-fill text-ink-dim',
          )}
        >
          {label}
        </div>
      );
    }
    return (
      <div className="px-4 py-2.5 rounded-full text-center text-xs font-semibold bg-fill text-ink-dim">
        No tasks today · enjoy the breather
      </div>
    );
  }

  const completed = data.tasks.filter((t) => t.status === 'complete').length;
  const currentIndex = data.active_task_id
    ? data.tasks.findIndex((t) => t.id === data.active_task_id) + 1
    : completed + 1;

  // Honest, current-moment read of behind/on-schedule/ahead (replaces the
  // old hard-coded "on schedule").
  const sched = computeCrewScheduleStatus(data.tasks);
  const behind = sched.state === 'behind';

  const schedLabel =
    sched.state === 'behind'
      ? `behind by ${formatHoursMinutes(sched.minutes)}`
      : sched.state === 'ahead'
        ? `ahead by ${formatHoursMinutes(sched.minutes)}`
        : sched.state === 'done'
          ? 'all wrapped'
          : 'on schedule';

  const positionLabel =
    sched.state === 'done'
      ? `${completed} of ${totalTasks} done`
      : `Task ${Math.min(currentIndex, totalTasks)} of ${totalTasks}`;

  return (
    <div
      className={cn(
        'px-4 py-2.5 rounded-full text-center text-xs font-semibold',
        behind
          ? 'bg-status-requested text-status-requested-fg'
          : 'bg-status-complete text-status-complete-fg',
      )}
    >
      ● {positionLabel} · {schedLabel} ·{' '}
      {formatHoursMinutes(data.crewed_minutes_today)} crewed today
    </div>
  );
}

function TaskList({
  tasks,
  activeTaskId,
  nextTaskId,
  navigate,
  hasProjects = false,
}: {
  tasks: CrewTodayTask[];
  activeTaskId: string | null;
  nextTaskId: string | null;
  navigate: ReturnType<typeof useNavigate>;
  /** Projects render above; with any present, an empty task list is fine (no empty state). */
  hasProjects?: boolean;
}) {
  if (tasks.length === 0) {
    if (hasProjects) return null;
    return (
      <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim">
        Nothing scheduled for you today. Check{' '}
        <Link to="/my-week" className="text-accent font-medium">
          your week
        </Link>{' '}
        if you're expecting work.
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">
        {hasProjects ? 'Tasks' : 'Schedule'}
      </div>
      <div className="space-y-2">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            expanded={task.id === activeTaskId || (!activeTaskId && task.id === nextTaskId)}
            onOpenTask={() => navigate(`/today/tasks/${task.id}`)}
            onOpenProject={() => navigate(`/projects/${task.project_id}`)}
          />
        ))}
      </div>
    </div>
  );
}

function TaskCard({
  task,
  expanded,
  onOpenTask,
  onOpenProject,
}: {
  task: CrewTodayTask;
  expanded: boolean;
  onOpenTask: () => void;
  onOpenProject: () => void;
}) {
  const transition = useTransitionTask(task.id);
  const Icon = TYPE_ICON[task.type];

  // Visual state for the card
  const isComplete = task.status === 'complete';
  const isInProgress = ['in_progress', 'on_site', 'en_route'].includes(task.status);
  const isPaused = task.status === 'paused';
  const isBlocked = task.status === 'blocked';
  // Crew can undo a just-tapped Finish within the grace window (demo #7). No live
  // ticker on this list — gated at render; the server enforces the same window.
  const canUndo =
    isComplete &&
    task.completed_at != null &&
    Date.now() - parseISO(task.completed_at).getTime() < UNDO_GRACE_MS;

  const visualClass = isComplete
    ? 'bg-status-complete text-status-complete-fg border-status-complete-fg/20'
    : isInProgress
      ? 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/40 ring-1 ring-status-onsite-fg/30'
      : isPaused
        ? 'bg-status-scheduled text-status-scheduled-fg border-status-scheduled-fg/30'
        : isBlocked
          ? 'bg-status-blocked text-status-blocked-fg border-status-blocked-fg/40'
          : 'bg-bg border-line';

  const subtypeLabel =
    task.type === 'pickup'
      ? task.pickup_subtype
      : task.type === 'install'
        ? task.install_subtype
        : null;

  // Two-button crew flow (Start / Finish) — demo #7. No GPS/ETA capture (the
  // en route stage + public ETA were retired); changes still queue offline.
  function advance(to: 'in_progress' | 'complete') {
    transition.mutate(
      { to },
      {
        onSuccess: (res) =>
          isQueuedResult(res)
            ? toast.success(
                `Saved offline — "${to.replace('_', ' ')}" will sync when you reconnect`,
              )
            : toast.success(`Marked ${to.replace('_', ' ')}`),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  if (!expanded) {
    // Collapsed row — whole row taps through to the task detail.
    return (
      <button
        onClick={onOpenTask}
        className={cn(
          'flex items-center gap-3 p-3 rounded-xl border text-sm w-full text-left',
          visualClass,
        )}
      >
        <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {isComplete && '✓ '}
            {timeOnly(task.scheduled_start)} · {task.title}
          </div>
          <div className="text-xs opacity-75 truncate">
            {task.project_title}
            {subtypeLabel && ` · ${subtypeLabel}`}
          </div>
        </div>
        {task.crew.length > 0 && <CrewAvatars crew={task.crew} max={3} className="shrink-0" />}
        <ChevronRight className="w-4 h-4 opacity-60 flex-shrink-0" />
      </button>
    );
  }

  // Expanded card
  return (
    <div className={cn('p-4 rounded-2xl border', visualClass)}>
      <button onClick={onOpenTask} className="flex items-start gap-2.5 mb-1 w-full text-left">
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 opacity-80" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base leading-tight">
            {isInProgress && '▶ '}
            {timeOnly(task.scheduled_start)} · {task.title}
          </div>
          <div className="text-xs opacity-80 mt-0.5">
            {task.project_title}
            {subtypeLabel && ` · ${subtypeLabel}`}
            {task.is_lead && ' · you lead'}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 mt-1 opacity-50 flex-shrink-0" />
      </button>

      {task.crew.length > 0 && (
        <CrewAvatars crew={task.crew} withNames className="mt-2" nameClass="opacity-80" />
      )}

      {task.site_short_address && (
        <div className="flex items-center gap-1.5 text-xs opacity-80 mt-2">
          <MapPin className="w-3 h-3" /> {task.site_short_address}
        </div>
      )}

      {task.project_day_window && (
        <div className="flex items-center gap-1.5 text-xs opacity-80 mt-1.5">
          <Clock className="w-3 h-3" /> Hours today ·{' '}
          {fmtDayWindow(task.project_day_window)}
        </div>
      )}

      {task.description && (
        <div className="text-xs opacity-85 mt-2 whitespace-pre-wrap">{task.description}</div>
      )}

      {task.sap_rma_reference && (
        <div className="text-[11px] opacity-85 mt-2 font-mono">
          RMA · {task.sap_rma_reference}
        </div>
      )}

      {/* Action buttons. h-11 (44px) is the floor for gloved/sunlit field taps.
          Two-button crew flow (demo #7): Start a scheduled task, Finish when done
          — the en route / on site / pause stages were retired. */}
      <div className="flex gap-2 mt-4">
        {(task.status === 'scheduled' || task.status === 'en_route') && (
          <Button size="sm" className="flex-1 h-11" onClick={() => advance('in_progress')} disabled={transition.isPending}>
            <Play className="w-3.5 h-3.5" /> Start
          </Button>
        )}
        {(task.status === 'in_progress' || task.status === 'on_site' || isPaused) && (
          <Button size="sm" className="flex-1 h-11" onClick={() => advance('complete')} disabled={transition.isPending}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Finish
          </Button>
        )}
        {canUndo && (
          <Button size="sm" variant="outline" className="flex-1 h-11" onClick={() => advance('in_progress')} disabled={transition.isPending}>
            <RotateCcw className="w-3.5 h-3.5" /> Undo finish
          </Button>
        )}
      </div>

      {/* Open project link */}
      <button
        onClick={onOpenProject}
        className="flex items-center justify-between w-full mt-3 pt-3 border-t border-current/10 text-xs opacity-75 hover:opacity-100"
      >
        <span>Open project · {task.project_client_name}</span>
        <ChevronRight className="w-3.5 h-3.5" />
      </button>

      {isBlocked && (
        <div className="flex items-center gap-2 mt-3 text-xs">
          <AlertTriangle className="w-3.5 h-3.5" /> Blocked — supervisor notified.
        </div>
      )}
    </div>
  );
}

function NoCrewMemberState({ role }: { role?: string }) {
  const isSupervisor = role === 'supervisor' || role === 'admin';
  return (
    <div className="h-full grid place-items-center bg-fill-soft p-6">
      <div className="max-w-sm text-center space-y-3">
        <h1 className="text-xl font-semibold">Today is for the crew</h1>
        <p className="text-sm text-ink-dim">
          This screen shows a crew member their day in the field. Your account
          isn&apos;t linked to a crew profile.
        </p>
        <p className="text-sm">
          {isSupervisor ? (
            <Link to="/dispatch" className="text-accent font-medium hover:underline">
              Go to the dispatch board →
            </Link>
          ) : (
            <Link to="/" className="text-accent font-medium hover:underline">
              Back to home →
            </Link>
          )}
        </p>
      </div>
    </div>
  );
}

// NOTE: the wireframe's 5-tab bottom nav (Schedule / Photos / Messages / Me)
// was removed rather than ship dead tabs — Today is the single real crew
// surface today, and each task taps through to its own detail screen. The
// nav returns once those destinations are real (Pillar B.3 photos, F.3).
